import React from 'react'

import { NextSeo, OrganizationJsonLd } from 'next-seo'
import Head from 'next/head'

import {
  BRANCH_NAME,
  FRONTEND_URL,
  IS_PROD_LIKE_ENVIRONMENT,
  IS_STATIC_EXPORT,
} from '@/constants/env'
import { SUPPORT_EMAIL } from '@/constants/general'

import type { NextPageWithLayout } from '@/types/next'

const META_DESCRIPTION =
  'The all-in-one government sales platform, powered by AI. Sweetspot leverages AI to help businesses source, manage, and respond to government contract opportunities.'

const DEFAULT_OG_IMAGE_URL = `${FRONTEND_URL}${
  IS_STATIC_EXPORT ? '/logo.png' : '/api/og'
}`

// Helper to prefix title with branch name for multi-instance dev
// Only applies in local/dev environments, never in production
const formatTitle = (title: string): string => {
  if (BRANCH_NAME && !IS_PROD_LIKE_ENVIRONMENT) {
    return `[${BRANCH_NAME}] ${title}`
  }
  return title
}

interface DefaultMetadataProps {
  title?: string
  description?: string
}

export const DefaultMetadata: React.FC<DefaultMetadataProps> = React.memo(
  ({ title, description }) => {
    const displayTitle = formatTitle(title ?? 'Sweetspot')

    return (
      <>
        <NextSeo
          title={displayTitle}
          description={description ?? META_DESCRIPTION}
          additionalLinkTags={[
            {
              rel: 'icon',
              href: `${FRONTEND_URL}/favicon.ico`,
            },
          ]}
          openGraph={{
            description: description ?? META_DESCRIPTION,
            images: [
              {
                url: DEFAULT_OG_IMAGE_URL,
                width: 1200,
                height: 632,
                alt: 'Sweetspot',
              },
            ],
            siteName: 'Sweetspot',
            title: displayTitle,
            type: 'website',
            url: FRONTEND_URL,
          }}
          twitter={{
            handle: '@SweetspotGov',
            site: '@SweetspotGov',
            cardType: 'summary_large_image',
          }}
        />
        <OrganizationJsonLd
          type="Corporation"
          id={`${FRONTEND_URL}#corporation`}
          logo={`${FRONTEND_URL}/logo.png`}
          legalName="Sweetspot Chat, Inc."
          name="Sweetspot"
          address={{
            streetAddress: '633 Bloomfield St',
            addressLocality: 'Hoboken',
            addressRegion: 'NJ',
            postalCode: '07030',
            addressCountry: 'USA',
          }}
          contactPoint={[
            {
              email: SUPPORT_EMAIL,
              contactType: 'customer service',
              areaServed: 'US',
              availableLanguage: ['English'],
            },
          ]}
          url={FRONTEND_URL}
        />
        {/*
          for some reason, Twitter cards using standard OG tags don't work, need to add this manually;
          https://github.com/garmeeh/next-seo/issues/14;
        */}
        <Head>
          <meta name="twitter:card" content="summary_large_image" />
          <meta property="twitter:title" content={displayTitle} />
          <meta property="twitter:image" content={DEFAULT_OG_IMAGE_URL} />
        </Head>
      </>
    )
  },
)

interface GetMetadataFnProps {
  title?: string
  description?: string
}

export const getMetadataFn =
  ({ title, description }: GetMetadataFnProps = {}) =>
  // oxlint-disable-next-line react/display-name
  () => <DefaultMetadata title={title} description={description} />

export const withDefaultMetadata = (
  Page: NextPageWithLayout,
  args: GetMetadataFnProps = {},
): NextPageWithLayout => {
  const WithMetadata: NextPageWithLayout = (
    props: React.ComponentProps<NextPageWithLayout>,
  ) => {
    return (
      <>
        <DefaultMetadata title={args.title} description={args.description} />
        <Page {...props} />
      </>
    )
  }
  return WithMetadata
}

DefaultMetadata.displayName = 'Seo.DefaultMetadata'
